<template>
    <div class="weight-field-display-edit-operator">
        <div v-if="readonly">
            {{ displayValue }}
        </div>
        <div v-else>
            <div v-if="showInput" class="weight-editor" @focusout="onFocusOut">
                <b-input-group size="sm" class="weight-group">
                    <b-input-group-prepend>
                        <b-form-select
                            v-model="fromOperator"
                            :options="fromOperatorOptions"
                            class="operator-select custom-select-sm"
                            @change="emitValue"
                        />
                    </b-input-group-prepend>
                    <b-form-input
                        ref="fromInput"
                        type="text"
                        :value="fromValue"
                        class="weight-input form-control-sm"
                        inputmode="decimal"
                        @focus="onInputFocus('from')"
                        @input="onFromInput"
                        @keyup.enter="onEnter"
                    />
                </b-input-group>
                <b-input-group size="sm" class="weight-group">
                    <b-input-group-prepend>
                        <b-form-select
                            v-model="toOperator"
                            :options="toOperatorOptions"
                            class="operator-select custom-select-sm"
                            @change="emitValue"
                        />
                    </b-input-group-prepend>
                    <b-form-input
                        type="text"
                        :value="toValue"
                        class="weight-input form-control-sm"
                        inputmode="decimal"
                        @focus="onInputFocus('to')"
                        @input="onToInput"
                        @keyup.enter="onEnter"
                    />
                </b-input-group>
            </div>
            <button
                v-else
                type="button"
                class="field-link"
                @click="openEditor"
            >
                {{ displayValue }}
            </button>
        </div>
    </div>
</template>

<script>
import { BFormInput, BFormSelect, BInputGroup, BInputGroupPrepend } from 'bootstrap-vue'

export default {
    name: 'WeightFieldDisplayEditOperator',
    components: {
        BFormInput,
        BFormSelect,
        BInputGroup,
        BInputGroupPrepend,
    },
    props: {
        value: {
            type: Object,
            default: null,
        },
        alwaysEdit: {
            type: Boolean,
            default: false,
        },
        unit: {
            type: String,
            default: '% (w/w)',
        },
        readonly: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        const initial = this.normalizeValue(this.value)
        return {
            showInput: this.alwaysEdit ? true : !this.hasAnyValue(initial),
            fromValue: initial.fromText,
            toValue: initial.toText,
            fromOperator: initial.fromOperator,
            toOperator: initial.toOperator,
            inputDebounceTimer: null,
            activeInput: null,
        }
    },
    computed: {
        fromOperatorOptions() {
            return [
                { value: 'GT', text: '>' },
                { value: 'GTE', text: '>=' },
            ]
        },
        toOperatorOptions() {
            return [
                { value: 'LT', text: '<' },
                { value: 'LTE', text: '<=' },
            ]
        },
        displayValue() {
            const payload = this.buildPayload()
            const from = payload.from
            const to = payload.to
            const fromOp = this.formatOperatorSymbol(payload.fromOperator || 'GTE')
            const toOp = this.formatOperatorSymbol(payload.toOperator || 'LTE')
            const unit = payload.unit || this.unit
            if (from === null && to === null) {
                return '-'
            }
            if (from !== null && to !== null) {
                if (from === to) {
                    return `${fromOp} ${from} ${unit}`
                }
                return `${fromOp} ${from} - ${toOp} ${to} ${unit}`
            }
            if (from !== null) {
                return `${fromOp} ${from} ${unit}`
            }
            return `${toOp} ${to} ${unit}`
        },
    },
    watch: {
        value(next) {
            const normalized = this.normalizeValue(next)
            if (this.activeInput !== 'from') {
                this.fromValue = normalized.fromText
            }
            if (this.activeInput !== 'to') {
                this.toValue = normalized.toText
            }
            this.fromOperator = normalized.fromOperator
            this.toOperator = normalized.toOperator
            if (!this.hasAnyValue(normalized)) {
                this.showInput = true
            }
        },
        alwaysEdit(next) {
            if (next) {
                this.showInput = true
            }
        },
    },
    methods: {
        normalizePartialRange(payload) {
            const normalized = payload || {}
            const hasFrom = !(normalized.from === null || normalized.from === undefined || normalized.from === '')
            const hasTo = !(normalized.to === null || normalized.to === undefined || normalized.to === '')

            if (!hasFrom && hasTo) {
                return {
                    ...normalized,
                    from: 0,
                    fromOperator: 'GT',
                }
            }

            return normalized
        },
        formatOperatorSymbol(value) {
            const normalized = String(value || '').toUpperCase()
            const map = {
                GT: '>',
                GTE: '>=',
                LT: '<',
                LTE: '<=',
            }
            return map[normalized] || value
        },
        normalizeOperator(value, fallback) {
            if (!value) {
                return fallback
            }
            const normalized = String(value).toUpperCase()
            if (['GT', 'GTE', 'LT', 'LTE'].includes(normalized)) {
                return normalized
            }
            if (value === '>' || value === '>=') {
                return value === '>' ? 'GT' : 'GTE'
            }
            if (value === '<' || value === '<=') {
                return value === '<' ? 'LT' : 'LTE'
            }
            return fallback
        },
        onFocusOut(event) {
            const next = event && event.relatedTarget
            const root = this.$el
            if (next && root && root.contains(next)) {
                return
            }
            this.activeInput = null
            this.onBlur()
        },
        onInputFocus(input) {
            this.activeInput = input
        },
        normalizeValue(value) {
            const raw = value || {}
            const from = raw.from !== null && raw.from !== undefined ? raw.from : null
            const to = raw.to !== null && raw.to !== undefined ? raw.to : null
            return {
                fromText: from === null ? '' : String(from),
                toText: to === null ? '' : String(to),
                fromOperator: this.normalizeOperator(raw.fromOperator, 'GTE'),
                toOperator: this.normalizeOperator(raw.toOperator, 'LTE'),
            }
        },
        hasAnyValue(normalized) {
            return Boolean((normalized.fromText && String(normalized.fromText).trim())
                || (normalized.toText && String(normalized.toText).trim()))
        },
        onFromInput(next) {
            this.fromValue = this.sanitizeInput(next)
            this.scheduleEmit()
        },
        onToInput(next) {
            this.toValue = this.sanitizeInput(next)
            this.scheduleEmit()
        },
        scheduleEmit() {
            if (this.inputDebounceTimer) {
                clearTimeout(this.inputDebounceTimer)
            }
            this.inputDebounceTimer = setTimeout(() => {
                this.emitValue()
                this.inputDebounceTimer = null
            }, 600)
        },
        emitValue(allowIncompleteDecimal = false) {
            if (!allowIncompleteDecimal && this.hasIncompleteDecimal()) {
                return
            }
            const payload = this.normalizePartialRange(this.buildPayload())
            if (allowIncompleteDecimal) {
                this.syncFromPayload(payload)
            }
            this.$emit('input', payload)
        },
        buildPayload() {
            return {
                from: this.parseNumber(this.fromValue),
                to: this.parseNumber(this.toValue),
                fromOperator: this.normalizeOperator(this.fromOperator, 'GTE'),
                toOperator: this.normalizeOperator(this.toOperator, 'LTE'),
                unit: this.unit,
            }
        },
        syncFromPayload(payload) {
            const normalized = this.normalizeValue(payload)
            this.fromValue = normalized.fromText
            this.toValue = normalized.toText
            this.fromOperator = normalized.fromOperator
            this.toOperator = normalized.toOperator
        },
        onBlur() {
            if (this.inputDebounceTimer) {
                clearTimeout(this.inputDebounceTimer)
                this.inputDebounceTimer = null
            }
            this.emitValue(true)
            if (this.alwaysEdit) {
                return
            }
            if (this.hasAnyValue({ fromText: this.fromValue, toText: this.toValue })) {
                this.showInput = false
            }
        },
        onEnter() {
            this.onBlur()
            this.$emit('enter')
        },
        openEditor() {
            this.showInput = true
            this.$nextTick(() => {
                if (this.$refs.fromInput && this.$refs.fromInput.focus) {
                    this.$refs.fromInput.focus()
                }
            })
        },
        sanitizeInput(value) {
            if (value === null || value === undefined) {
                return ''
            }
            return String(value).replace(/,/g, '.').replace(/\s+/g, '')
        },
        hasIncompleteDecimal() {
            return [this.fromValue, this.toValue].some(value => /^\d+\.$/.test(String(value || '').trim()))
        },
        parseNumber(value) {
            if (value === null || value === undefined) {
                return null
            }
            const raw = String(value).trim().replace(/,/g, '.')
            if (!raw) {
                return null
            }
            const parsed = Number(raw)
            return Number.isNaN(parsed) ? null : parsed
        },
    },
    beforeDestroy() {
        if (this.inputDebounceTimer) {
            clearTimeout(this.inputDebounceTimer)
            this.inputDebounceTimer = null
        }
    },
}
</script>

<style scoped>
.field-link {
    background: transparent;
    border: 0;
    padding: 0;
    color: #2b6cb0;
    text-decoration: underline;
    cursor: pointer;
    text-align: left;
}
.field-link:hover {
    color: #1e4e8c;
}
.weight-editor {
    display: flex;
    flex-direction: row;
    gap: 6px;
    align-items: center;
}
.weight-group {
    max-width: 200px;
}
.operator-select {
    min-width: 54px;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
}
.weight-input {
    min-width: 80px;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
}
</style>
